import { supabase } from './supabase';

// White-label firm console data access. Clients / branding / dockets / queue
// are read+written DIRECTLY via supabase (RLS scopes everything to the
// caller's firm via firm_members — same idiom as docketFollowService). Only
// "send" hits the backend, which runs as service-role (emails the client +
// records deliveries + enforces membership server-side).
const BACKEND_URL =
  import.meta.env.VITE_BACKEND_API_URL ||
  'https://fcc-summarize-app-r4ac3.ondigitalocean.app';

export interface Firm {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export interface FirmMembership {
  firm_id: string;
  role: string;
  firm: Firm;
}

export interface FirmBranding {
  firm_id: string;
  display_name: string | null;
  logo_url: string | null;
  primary_color: string | null;
  footer_html: string | null;
  reply_to_email: string | null;
  address_block: string | null;
  upl_disclaimer: string | null;
  attorney_name: string | null;
}

export interface FirmClient {
  id: string;
  firm_id: string;
  display_name: string;
  primary_email: string | null;
  status: 'active' | 'paused' | 'unsubscribed';
  contact_name: string | null;
  description: string | null;
  business_description: string | null;
  historical_dockets: string[] | null;
  filing_types: string[] | null;
  created_at: string;
}

export interface ClientRecipient {
  id: string;
  client_id: string;
  firm_id: string;
  email: string;
  unsubscribed_at: string | null;
}

export interface ClientDocket {
  id: string;
  client_id: string;
  docket_number: string;
  digest_frequency: string;
  notify_new_filings: boolean;
  last_notified_at: string | null;
}

export interface QueuePayload {
  subject?: string;
  html?: string;
  text?: string;
  total_filings?: number;
  dockets?: string[];
  // The exact email that went out (incl. cover note), snapshotted at send.
  sent_html?: string;
  sent_text?: string;
}

export interface QueueItem {
  id: string;
  firm_id: string;
  client_id: string;
  status: string;
  created_at: string;
  sent_at?: string | null;
  cover_note?: string | null;
  payload: QueuePayload | null;
}

// Filing types a client's digest can be scoped to. Empty = all.
export const FILING_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'EX_PARTE', label: 'Ex Parte' },
  { value: 'COMMENT', label: 'Comment' },
  { value: 'REPLY COMM', label: 'Reply Comment' },
  { value: 'STATEMENT', label: 'Statement' },
  { value: 'ORDER', label: 'Order' },
  { value: 'LETTER', label: 'Letter' },
  { value: 'NOTICE', label: 'Notice' },
];

class FirmService {
  private static instance: FirmService;
  static getInstance(): FirmService {
    if (!FirmService.instance) FirmService.instance = new FirmService();
    return FirmService.instance;
  }

  /** The caller's firm membership, or null if they're not a firm member. */
  async getMyFirm(): Promise<FirmMembership | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('firm_members')
      .select('firm_id, role, firm:firms(id, name, slug, status)')
      .eq('user_id', user.id)
      .limit(1)
      .maybeSingle();
    // Distinguish a genuine non-member (no row) from a transient query failure:
    // throwing on error lets callers keep a firm member out of a false "not a member"
    // bounce when the lookup itself failed.
    if (error) throw error;
    if (!data) return null;
    return data as unknown as FirmMembership;
  }

  async getStats(firmId: string): Promise<{ clients: number; pending: number; sent: number }> {
    const [c, p, s] = await Promise.all([
      supabase.from('clients').select('id', { count: 'exact', head: true }).eq('firm_id', firmId),
      supabase
        .from('firm_digest_queue')
        .select('id', { count: 'exact', head: true })
        .eq('firm_id', firmId)
        .eq('status', 'pending'),
      supabase
        .from('firm_digest_queue')
        .select('id', { count: 'exact', head: true })
        .eq('firm_id', firmId)
        .eq('status', 'sent'),
    ]);
    return { clients: c.count ?? 0, pending: p.count ?? 0, sent: s.count ?? 0 };
  }

  // --- Branding ---------------------------------------------------------
  async getBranding(firmId: string): Promise<FirmBranding | null> {
    const { data } = await supabase
      .from('firm_branding')
      .select('*')
      .eq('firm_id', firmId)
      .maybeSingle();
    return (data as FirmBranding) || null;
  }

  async updateBranding(firmId: string, patch: Partial<FirmBranding>): Promise<void> {
    const { error } = await supabase
      .from('firm_branding')
      .upsert({ firm_id: firmId, ...patch }, { onConflict: 'firm_id' });
    if (error) throw error;
  }

  // --- Clients ----------------------------------------------------------
  async listClients(firmId: string): Promise<FirmClient[]> {
    const { data } = await supabase
      .from('clients')
      .select('*')
      .eq('firm_id', firmId)
      .order('created_at', { ascending: false });
    return (data as FirmClient[]) || [];
  }

  async createClient(firmId: string, c: Partial<FirmClient>): Promise<FirmClient> {
    const { data, error } = await supabase
      .from('clients')
      .insert({ firm_id: firmId, ...c })
      .select()
      .single();
    if (error) throw error;
    return data as FirmClient;
  }

  async updateClient(clientId: string, patch: Partial<FirmClient>): Promise<void> {
    const { error } = await supabase.from('clients').update(patch).eq('id', clientId);
    if (error) throw error;
  }

  // --- Additional recipients (company CC list) --------------------------
  async listClientRecipients(clientId: string): Promise<ClientRecipient[]> {
    const { data } = await supabase
      .from('client_recipients')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: true });
    return (data as ClientRecipient[]) || [];
  }

  async addClientRecipient(firmId: string, clientId: string, email: string): Promise<void> {
    const { error } = await supabase
      .from('client_recipients')
      .insert({ firm_id: firmId, client_id: clientId, email: email.trim() });
    if (error) throw error;
  }

  async removeClientRecipient(id: string): Promise<void> {
    const { error } = await supabase.from('client_recipients').delete().eq('id', id);
    if (error) throw error;
  }

  // --- Client dockets ---------------------------------------------------
  async listClientDockets(clientId: string): Promise<ClientDocket[]> {
    const { data } = await supabase
      .from('docket_follows')
      .select('*')
      .eq('client_id', clientId)
      .order('followed_at', { ascending: false });
    return (data as ClientDocket[]) || [];
  }

  async addClientDocket(
    firmId: string,
    clientId: string,
    docketNumber: string,
    frequency = 'daily',
  ): Promise<void> {
    const { error } = await supabase.from('docket_follows').insert({
      firm_id: firmId,
      client_id: clientId,
      docket_number: docketNumber.trim(),
      digest_frequency: frequency,
    });
    if (error) throw error;
  }

  async removeClientDocket(followId: string): Promise<void> {
    const { error } = await supabase.from('docket_follows').delete().eq('id', followId);
    if (error) throw error;
  }

  /** Set how far back this client's FIRST digest reaches, by setting the
   *  "since" floor on its follows. `days` = null means full history; otherwise
   *  the last N days. Firm-membership-scoped RPC. Returns follows updated. */
  async setClientInitialWindow(clientId: string, days: number | null): Promise<number> {
    const { data, error } = await supabase.rpc('set_client_initial_window', {
      p_client_id: clientId,
      p_days: days,
    });
    if (error) throw error;
    return (data as number) ?? 0;
  }

  // --- Review queue -----------------------------------------------------
  async listPendingQueue(firmId: string): Promise<QueueItem[]> {
    const { data } = await supabase
      .from('firm_digest_queue')
      .select('*')
      .eq('firm_id', firmId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    return (data as QueueItem[]) || [];
  }

  async listSentDigests(firmId: string): Promise<QueueItem[]> {
    const { data } = await supabase
      .from('firm_digest_queue')
      .select('id, firm_id, client_id, status, payload, created_at, sent_at, cover_note')
      .eq('firm_id', firmId)
      .eq('status', 'sent')
      .order('sent_at', { ascending: false })
      .limit(100);
    return (data as QueueItem[]) || [];
  }

  async discardQueued(queueId: string): Promise<void> {
    const { error } = await supabase
      .from('firm_digest_queue')
      .update({ status: 'discarded' })
      .eq('id', queueId);
    if (error) throw error;
  }

  /** Send a reviewed digest. Runs server-side (emails client + records
   *  deliveries); the backend re-checks firm membership from the JWT. */
  async sendQueued(queueId: string, coverNote: string | null): Promise<any> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const resp = await fetch(`${BACKEND_URL}/api/firm/queue/${queueId}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ cover_note: coverNote || null }),
    });
    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(t || `Send failed (${resp.status})`);
    }
    return resp.json();
  }
}

export const firmService = FirmService.getInstance();
